/**
 * /leads/portfolio/<tag> — operator review page for a portfolio's demand
 * taxonomy.
 *
 * Per docs/design-portfolio-demand-onboarding.md §5:
 *   - One card per cluster, with overlaid normalised daily-mean curves
 *     of the HH-having members so the operator can visually verify
 *     coherence.
 *   - Drag-and-drop between cards lets the operator fix Claude
 *     misclusterings.
 *   - Representative radio button per cluster (highlights the picked
 *     curve on the chart).
 *   - "Regenerate" recomputes via the AI model; "Apply" writes the
 *     resolved assignment to every cluster member.
 */
import {
  AssignDemandResponse,
  RemoveInferredDemandResponse,
  Taxonomy,
  TaxonomyCluster,
  TaxonomyInputSite,
  useAsyncOperationWithResult,
  useLeadPortfolio,
  useLeadPortfolioMutations,
  useLeadPortfolioTaxonomyInput,
  useLeadPortfolioTaxonomyTrace,
  useSiteChartDataMany,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import ReactECharts from "echarts-for-react";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Spinner,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

interface DailyMeanCurve {
  daily_mean_normalised: number[];
  has_hh_data: boolean;
  annual_kwh: number | null;
}

/** A working copy of the taxonomy that the operator can edit locally
 *  (drag-between-clusters, pick representative, change fallback) before
 *  hitting Apply. Mirrors `TaxonomyCluster` but lets us mutate freely. */
interface EditableCluster {
  id: string;
  label: string;
  rationale: string;
  memberSiteIds: string[];
  representativeSiteId: string | null;
  fallbackProfile: TaxonomyCluster["fallback_profile"];
  coherenceWarning: string | null;
}

const toEditable = (t: Taxonomy): EditableCluster[] =>
  t.clusters.map((c) => ({
    id: c.id,
    label: c.label,
    rationale: c.rationale,
    memberSiteIds: [...c.member_site_ids],
    representativeSiteId: c.proposed_representative_site_id,
    fallbackProfile: c.fallback_profile,
    coherenceWarning: c.coherence_warning,
  }));

const PortfolioReviewPage: React.FC = () => {
  const { tag } = useParams<{ tag: string }>();
  if (!tag) return <div>Missing portfolio tag in URL.</div>;
  return <PortfolioReviewPageInner tag={tag} />;
};

const PortfolioReviewPageInner: React.FC<{ tag: string }> = ({ tag }) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const portfolio = useLeadPortfolio(tag);
  const taxonomyInput = useLeadPortfolioTaxonomyInput(tag);
  const {
    regenerateTaxonomy,
    assignDemand,
    removeInferredDemand,
    saveTaxonomy,
  } = useLeadPortfolioMutations(tag);

  // Regenerate is a tier-2 LRO (Bedrock clustering): kick it off, then poll for the taxonomy.
  const regenOp = useAsyncOperationWithResult<Taxonomy & { error?: string }>({
    siteId: tag,
  });
  // Assign + remove are tier-2 LROs too (each recomputes many sites via update_site).
  const assignOp = useAsyncOperationWithResult<AssignDemandResponse>({
    siteId: tag,
  });
  const removeOp = useAsyncOperationWithResult<RemoveInferredDemandResponse>({
    siteId: tag,
  });
  // The completed assign/remove payloads, held for the result alerts after the op result clears.
  const [assignResult, setAssignResult] = useState<AssignDemandResponse | null>(
    null
  );
  const [removeResult, setRemoveResult] =
    useState<RemoveInferredDemandResponse | null>(null);

  const sitesById = useMemo(() => {
    const m: Record<string, TaxonomyInputSite> = {};
    taxonomyInput.data?.sites.forEach((s) => {
      m[s.site_id] = s;
    });
    return m;
  }, [taxonomyInput.data]);

  // Local editable taxonomy state. Initialised when the portfolio metadata
  // loads (we read taxonomyJson off the metadata row, not the regenerate
  // response, so reopening the page after a previous regenerate restores
  // the cached taxonomy). The operator's in-progress edits live here until
  // they hit Apply.
  const [clusters, setClusters] = useState<EditableCluster[]>([]);
  const [dirty, setDirty] = useState(false);
  // Opt-in AI clustering trace (fetched only when the operator asks - it can be large).
  const [showTrace, setShowTrace] = useState(false);
  const taxonomyTrace = useLeadPortfolioTaxonomyTrace(tag, showTrace);
  // Key the reset on the stable `taxonomyMatchHash` instead of the raw
  // `taxonomyJson` reference: react-query returns a new object on every
  // refetch even if the payload is identical, which would otherwise blow
  // away the operator's in-progress edits whenever they hit any other
  // mutation that invalidates the query.
  const taxonomyMatchHash = portfolio.data?.taxonomyMatchHash;
  useEffect(() => {
    const tax = portfolio.data?.taxonomyJson;
    if (tax) {
      setClusters(toEditable(tax));
      setDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxonomyMatchHash]);

  // Consume the regenerate LRO result: load the fresh clusters + refresh the cached metadata so
  // the staleness banner clears. AIModelNotConfigured comes back as a completed op with an
  // `error` field; a failed op (parse/metadata error) surfaces as result.type === "error".
  const { result: regenResult, setResult: setRegenResult } = regenOp;
  useEffect(() => {
    if (!regenResult) return;
    if (regenResult.type === "error") {
      alert(`Regenerate failed: ${regenResult.message}`);
      setRegenResult(null);
      return;
    }
    const tax = regenResult.response;
    if (!tax) {
      // Completed op with no payload - clear so `regenerating` doesn't wedge the button.
      setRegenResult(null);
      return;
    }
    if (tax.error) {
      alert(`Regenerate failed: ${tax.error}`);
    } else {
      setClusters(toEditable(tax));
      setDirty(false);
      queryClient.invalidateQueries(["leadPortfolio", tag]);
      queryClient.invalidateQueries(["leadPortfolioTaxonomyInput", tag]);
    }
    setRegenResult(null);
  }, [regenResult, setRegenResult, queryClient, tag]);

  // Consume the assign + remove LRO results: surface the payload + refresh cached site state.
  const { result: assignOpResult, setResult: setAssignOpResult } = assignOp;
  useEffect(() => {
    if (!assignOpResult) return;
    if (assignOpResult.type === "error") {
      alert(`Apply failed: ${assignOpResult.message}`);
    } else if (assignOpResult.response) {
      setAssignResult(assignOpResult.response);
      setDirty(false);
      queryClient.invalidateQueries(["leadPortfolio", tag]);
      queryClient.invalidateQueries(["leadPortfolioTaxonomyInput", tag]);
      queryClient.invalidateQueries(["site"]);
      queryClient.invalidateQueries(["sites"]);
    }
    setAssignOpResult(null);
  }, [assignOpResult, setAssignOpResult, queryClient, tag]);

  const { result: removeOpResult, setResult: setRemoveOpResult } = removeOp;
  useEffect(() => {
    if (!removeOpResult) return;
    if (removeOpResult.type === "error") {
      alert(`Remove failed: ${removeOpResult.message}`);
    } else if (removeOpResult.response) {
      setRemoveResult(removeOpResult.response);
      queryClient.invalidateQueries(["leadPortfolio", tag]);
      queryClient.invalidateQueries(["leadPortfolioTaxonomyInput", tag]);
      queryClient.invalidateQueries(["site"]);
      queryClient.invalidateQueries(["sites"]);
    }
    setRemoveOpResult(null);
  }, [removeOpResult, setRemoveOpResult, queryClient, tag]);

  // Detect taxonomy staleness: current match hash vs the one cached at
  // the time of the last regenerate. Changes when site membership or HH
  // coverage shifts (e.g. operator added a new lead after the last run).
  const taxonomyIsStale =
    taxonomyInput.data &&
    portfolio.data?.taxonomyMatchHash &&
    taxonomyInput.data.match_hash !== portfolio.data.taxonomyMatchHash;

  if (portfolio.isLoading || taxonomyInput.isLoading) {
    return (
      <Container className="py-4">
        <Spinner animation="border" /> Loading portfolio…
      </Container>
    );
  }
  if (taxonomyInput.error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          Failed to load portfolio: {String(taxonomyInput.error)}
        </Alert>
      </Container>
    );
  }

  const sites = taxonomyInput.data?.sites ?? [];
  const hhCoverage = sites.filter((s) => s.has_hh_data).length;
  const hasTaxonomy = clusters.length > 0;

  // --- Mutations -----------------------------------------------------------

  const regenerating =
    regenerateTaxonomy.isLoading || !!regenOp.operationKey || !!regenResult;
  const applying =
    assignDemand.isLoading || !!assignOp.operationKey || !!assignOpResult;
  const removing =
    removeInferredDemand.isLoading ||
    !!removeOp.operationKey ||
    !!removeOpResult;

  const onRegenerate = () => {
    regenerateTaxonomy.mutate(undefined, {
      onSuccess: (kickoff) => regenOp.setOperationKey(kickoff.operationKey),
    });
  };

  const onApply = () => {
    setAssignResult(null);
    assignDemand.mutate(
      {
        clusters: clusters.map((c) => ({
          representativeSiteId: c.representativeSiteId,
          memberSiteIds: c.memberSiteIds,
          label: c.label,
        })),
      },
      { onSuccess: (kickoff) => assignOp.setOperationKey(kickoff.operationKey) }
    );
  };

  // Persist the operator's edited cluster structure (add/remove/move/relabel) as the cached
  // AI assisted site demand taxonomy, so the edits survive a reload.
  const onSaveTaxonomy = () => {
    saveTaxonomy.mutate(
      clusters.map((c) => ({
        id: c.id,
        label: c.label,
        rationale: c.rationale,
        member_site_ids: c.memberSiteIds,
        proposed_representative_site_id: c.representativeSiteId,
        fallback_profile: c.fallbackProfile,
        coherence_warning: c.coherenceWarning,
      })),
      { onSuccess: () => setDirty(false) }
    );
  };

  const onRemoveInferredDemand = () => {
    if (
      !window.confirm(
        "Remove inferred HH demand from every site in this portfolio? Sites with their own uploaded HH are kept."
      )
    )
      return;
    setRemoveResult(null);
    removeInferredDemand.mutate(undefined, {
      onSuccess: (kickoff) => removeOp.setOperationKey(kickoff.operationKey),
    });
  };

  const onAddCluster = () => {
    // crypto.randomUUID needs a secure context; the id only needs local uniqueness (React key +
    // drag target), so a timestamp+random fallback is fine on non-HTTPS preview hosts.
    const newId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setClusters((cs) => [
      ...cs,
      {
        id: `cluster-${newId}`,
        label: `New cluster ${cs.length + 1}`,
        rationale: "Operator-added cluster.",
        memberSiteIds: [],
        representativeSiteId: null,
        fallbackProfile: null,
        coherenceWarning: null,
      },
    ]);
    setDirty(true);
  };

  // Only empty clusters can be removed - drag a cluster's sites elsewhere first so no site is
  // silently dropped from the taxonomy.
  const onRemoveCluster = (clusterId: string) => {
    setClusters((cs) => cs.filter((c) => c.id !== clusterId));
    setDirty(true);
  };

  const onRelabelCluster = (clusterId: string, label: string) => {
    setClusters((cs) =>
      cs.map((c) => (c.id === clusterId ? { ...c, label } : c))
    );
    setDirty(true);
  };

  // --- Drag-and-drop between cluster cards --------------------------------

  const onDragStart = (e: React.DragEvent, siteId: string, fromClusterId: string) => {
    e.dataTransfer.setData(
      "application/inrange-cluster-move",
      JSON.stringify({ siteId, fromClusterId })
    );
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onDrop = (e: React.DragEvent, toClusterId: string) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/inrange-cluster-move");
    if (!raw) return;
    const { siteId, fromClusterId } = JSON.parse(raw) as {
      siteId: string;
      fromClusterId: string;
    };
    if (fromClusterId === toClusterId) return;
    setClusters((cs) =>
      cs.map((c) => {
        if (c.id === fromClusterId) {
          const newMembers = c.memberSiteIds.filter((id) => id !== siteId);
          // If we just removed the rep, clear it (operator can repick from remaining members).
          const newRep = c.representativeSiteId === siteId ? null : c.representativeSiteId;
          return { ...c, memberSiteIds: newMembers, representativeSiteId: newRep };
        }
        if (c.id === toClusterId) {
          return c.memberSiteIds.includes(siteId)
            ? c
            : { ...c, memberSiteIds: [...c.memberSiteIds, siteId] };
        }
        return c;
      })
    );
    setDirty(true);
  };

  const onPickRepresentative = (clusterId: string, siteId: string) => {
    setClusters((cs) =>
      cs.map((c) =>
        c.id === clusterId ? { ...c, representativeSiteId: siteId } : c
      )
    );
    setDirty(true);
  };

  // --- Render -------------------------------------------------------------

  return (
    <Container className="py-4">
      <div className="d-flex align-items-center gap-3 mb-3">
        <Button variant="link" onClick={() => navigate("/leads")}>
          ← Back to leads
        </Button>
        <h1 className="mb-0">
          Portfolio: {portfolio.data?.displayName ?? tag}
        </h1>
        <Badge bg="secondary">tag: {tag}</Badge>
      </div>

      <Alert variant="info" className="d-flex justify-content-between align-items-center">
        <div>
          <strong>{sites.length}</strong> sites in this portfolio.{" "}
          <strong>{hhCoverage}</strong> have half-hourly data.{" "}
          {hasTaxonomy ? (
            <span>{clusters.length} clusters proposed.</span>
          ) : (
            <span>
              No AI assisted site demand taxonomy yet — click Regenerate to run
              the AI clustering.
            </span>
          )}
          {taxonomyIsStale && (
            <span className="ms-2 text-warning">
              ⚠ AI assisted site demand taxonomy is out of date — regenerate to
              incorporate latest sites.
            </span>
          )}
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button
            variant="outline-primary"
            onClick={onRegenerate}
            disabled={regenerating || sites.length === 0}
          >
            {regenerating
              ? "Regenerating…"
              : "Regenerate AI assisted site demand taxonomy"}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={onAddCluster}
            disabled={!hasTaxonomy}
          >
            + Add cluster
          </Button>
          <Button
            variant="outline-primary"
            onClick={onSaveTaxonomy}
            disabled={!dirty || saveTaxonomy.isLoading || !hasTaxonomy}
          >
            {saveTaxonomy.isLoading ? "Saving…" : "Save taxonomy"}
          </Button>
          <Button
            variant="primary"
            onClick={onApply}
            disabled={applying || !hasTaxonomy}
          >
            {applying ? "Applying…" : "Apply demand assignments"}
          </Button>
          <Button
            variant="outline-danger"
            onClick={onRemoveInferredDemand}
            disabled={removing}
          >
            {removing ? "Removing…" : "Remove all inferred HH demand"}
          </Button>
          {hasTaxonomy && (
            <Button
              variant="outline-secondary"
              onClick={() => setShowTrace((s) => !s)}
            >
              {showTrace ? "Hide AI clustering trace" : "Show AI clustering trace"}
            </Button>
          )}
        </div>
      </Alert>

      {showTrace && (
        <Card body className="mb-3">
          <strong>AI clustering trace</strong>
          {taxonomyTrace.isLoading ? (
            <div className="mt-2 text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading trace…
            </div>
          ) : taxonomyTrace.data ? (
            <div className="mt-2">
              <AITraceViewer trace={taxonomyTrace.data} />
            </div>
          ) : (
            <div className="mt-2 text-muted">
              No AI trace available for this taxonomy (it may have been generated
              before traces were captured, or copied from another environment).
            </div>
          )}
        </Card>
      )}

      {regenerateTaxonomy.isError && (
        <Alert variant="danger">
          Regenerate failed: {String(regenerateTaxonomy.error)}
        </Alert>
      )}
      {assignDemand.isError && (
        <Alert variant="danger">
          Apply failed: {String(assignDemand.error)}
        </Alert>
      )}
      {saveTaxonomy.isError && (
        <Alert variant="danger">
          Save failed: {String(saveTaxonomy.error)}
        </Alert>
      )}
      {saveTaxonomy.isSuccess && (
        <Alert variant="success" dismissible>
          Saved AI assisted site demand taxonomy.
        </Alert>
      )}
      {removeInferredDemand.isError && (
        <Alert variant="danger">
          Remove failed: {String(removeInferredDemand.error)}
        </Alert>
      )}
      {removeResult && (
        <Alert variant="success" dismissible onClose={() => setRemoveResult(null)}>
          Removed inferred HH demand from {removeResult.resetSiteIds.length}{" "}
          site(s).
        </Alert>
      )}
      {assignResult && (
        <Alert
          variant={assignResult.updatedSiteIds.length === 0 ? "warning" : "success"}
          dismissible
          onClose={() => setAssignResult(null)}
        >
          {assignResult.updatedSiteIds.length === 0 &&
          assignResult.skipped.length === 0
            ? "No assignments to apply — pick a representative in each cluster first."
            : `Updated ${assignResult.updatedSiteIds.length} site(s) with inferred demand.`}
          {(() => {
            const updatedSet = new Set(assignResult.updatedSiteIds);
            const skipReason = new Map(
              assignResult.skipped.map((s) => [s.site_id, s.reason])
            );
            const nameOf = (id: string) =>
              sitesById[id]?.site_name ?? id.slice(0, 8);
            const groups = clusters
              .map((c) => ({
                id: c.id,
                label: c.label,
                members: c.memberSiteIds
                  .filter((id) => id !== c.representativeSiteId)
                  .filter((id) => updatedSet.has(id) || skipReason.has(id))
                  .map((id) => ({
                    id,
                    name: nameOf(id),
                    updated: updatedSet.has(id),
                    reason: skipReason.get(id),
                  })),
              }))
              .filter((g) => g.members.length > 0);
            if (groups.length === 0) return null;
            return (
              <div className="mt-2 small">
                {groups.map((g) => (
                  <div key={g.id} className="mb-2">
                    <strong>{g.label}</strong>
                    <ul className="mb-0">
                      {g.members.map((m) => (
                        <li key={m.id}>
                          {m.name}:{" "}
                          {m.updated ? (
                            <span className="text-success">
                              ✓ demand inferred
                            </span>
                          ) : (
                            <span className="text-muted">
                              skipped — {m.reason}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            );
          })()}
        </Alert>
      )}

      {!hasTaxonomy && sites.length === 0 && (
        <Alert variant="warning">
          No sites in this portfolio yet. Create sites from the leads tagged
          with <code>{tag}</code> first via /leads/bulk-create-sites.
        </Alert>
      )}

      <div className="d-flex flex-wrap gap-3">
        {clusters.map((cluster) => (
          <ClusterCard
            key={cluster.id}
            cluster={cluster}
            sitesById={sitesById}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, cluster.id)}
            onDragStart={onDragStart}
            onPickRepresentative={(siteId) =>
              onPickRepresentative(cluster.id, siteId)
            }
            onRemoveCluster={() => onRemoveCluster(cluster.id)}
            onRelabelCluster={(label) => onRelabelCluster(cluster.id, label)}
          />
        ))}
      </div>
    </Container>
  );
};

interface ClusterCardProps {
  cluster: EditableCluster;
  sitesById: Record<string, TaxonomyInputSite>;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragStart: (e: React.DragEvent, siteId: string, fromClusterId: string) => void;
  onPickRepresentative: (siteId: string) => void;
  onRemoveCluster: () => void;
  onRelabelCluster: (label: string) => void;
}

const ClusterCard: React.FC<ClusterCardProps> = ({
  cluster,
  sitesById,
  onDragOver,
  onDrop,
  onDragStart,
  onPickRepresentative,
  onRemoveCluster,
  onRelabelCluster,
}) => {
  const memberSites = cluster.memberSiteIds
    .map((id) => sitesById[id])
    .filter(Boolean);
  const hhSites = memberSites.filter((s) => s.has_hh_data);
  const namesById = Object.fromEntries(
    memberSites.map((s) => [s.site_id, s.site_name])
  );

  // Cluster name reads as plain text by default; the pencil toggles an inline editor so
  // an accidental click doesn't put every card into edit mode.
  const [isEditingLabel, setIsEditingLabel] = useState(false);

  return (
    <Card
      style={{ minWidth: 380, flex: "1 1 380px" }}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <Card.Header>
        <div className="d-flex align-items-center gap-2">
          {isEditingLabel ? (
            <input
              className="form-control form-control-sm fw-bold"
              style={{ maxWidth: 200 }}
              value={cluster.label}
              onChange={(e) => onRelabelCluster(e.target.value)}
              onBlur={() => setIsEditingLabel(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Escape")
                  setIsEditingLabel(false);
              }}
              aria-label="Cluster name"
              autoFocus
            />
          ) : (
            <>
              <strong>{cluster.label}</strong>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none"
                onClick={() => setIsEditingLabel(true)}
                aria-label="Edit cluster name"
                title="Edit cluster name"
              >
                ✏️
              </Button>
            </>
          )}
          <Badge bg="secondary">{memberSites.length} sites</Badge>
          <Badge bg={hhSites.length > 0 ? "success" : "warning"}>
            {hhSites.length} with HH
          </Badge>
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-auto"
            onClick={onRemoveCluster}
            disabled={memberSites.length > 0}
            title={
              memberSites.length > 0
                ? "Drag this cluster's sites elsewhere before removing it"
                : "Remove this empty cluster"
            }
          >
            Remove
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <p className="text-muted small mb-2">{cluster.rationale}</p>
        {cluster.coherenceWarning && (
          <Alert variant="warning" className="py-1 small">
            {cluster.coherenceWarning}
          </Alert>
        )}

        <OverlaidCurvesChart
          siteIds={hhSites.map((s) => s.site_id)}
          highlightedSiteId={cluster.representativeSiteId}
          namesById={namesById}
        />

        <div className="mt-2">
          <div className="small text-muted mb-1">
            Members — drag rows between cards to re-cluster. Pick a
            representative (radio) to set the cluster&apos;s density. The grey
            text is each site&apos;s demand description from the uploaded CSV.
          </div>
          {memberSites.map((s) => (
            <div
              key={s.site_id}
              draggable
              onDragStart={(e) => onDragStart(e, s.site_id, cluster.id)}
              className="d-flex align-items-center gap-2 py-1 px-2 rounded"
              style={{ cursor: "grab", borderBottom: "1px solid #eee" }}
            >
              <input
                type="radio"
                name={`rep-${cluster.id}`}
                checked={cluster.representativeSiteId === s.site_id}
                disabled={!s.has_hh_data}
                onChange={() => onPickRepresentative(s.site_id)}
              />
              <div className="flex-grow-1">
                <div>{s.site_name}</div>
                {(s.building_use || s.voa_primary_description) && (
                  <div className="text-muted small">
                    {s.building_use || s.voa_primary_description}
                  </div>
                )}
              </div>
              {s.has_hh_data ? (
                <Badge bg="success">HH</Badge>
              ) : s.has_inferred_hh_data ? (
                <Badge bg="info" title="Demand inferred from a representative">
                  HH inferred
                </Badge>
              ) : (
                <Badge bg="secondary">no HH</Badge>
              )}
            </div>
          ))}
        </div>
        {hhSites.length === 0 && cluster.fallbackProfile && (
          <Alert variant="info" className="mt-2 py-1 small">
            No HH siblings in this cluster. Fallback profile:{" "}
            <strong>{cluster.fallbackProfile.buildingProfile}</strong> ×{" "}
            <strong>{cluster.fallbackProfile.profileShape}</strong> ×{" "}
            <strong>{cluster.fallbackProfile.profileDays}</strong>
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
};

/** Fetches normalised daily-mean curves for the provided site ids and
 *  renders them as overlaid lines. The representative (if any) is drawn
 *  on top with full opacity; siblings sit underneath at 40% opacity so
 *  the operator can see how cohesive the cluster is. */
const OverlaidCurvesChart: React.FC<{
  siteIds: string[];
  highlightedSiteId: string | null;
  namesById: Record<string, string>;
}> = ({ siteIds, highlightedSiteId, namesById }) => {
  if (siteIds.length === 0) {
    return (
      <div className="text-muted small text-center py-3 border rounded">
        No HH-having members in this cluster.
      </div>
    );
  }
  return (
    <OverlaidCurvesChartLoaded
      siteIds={siteIds}
      highlightedSiteId={highlightedSiteId}
      namesById={namesById}
    />
  );
};

const OverlaidCurvesChartLoaded: React.FC<{
  siteIds: string[];
  highlightedSiteId: string | null;
  namesById: Record<string, string>;
}> = ({ siteIds, highlightedSiteId, namesById }) => {
  const { data, isLoading } = useSiteChartDataMany<DailyMeanCurve>(
    siteIds,
    "demand_curve_daily_mean"
  );

  const curves: Record<string, number[]> = {};
  if (data) {
    for (const sid of Object.keys(data)) {
      const payload = data[sid];
      if (payload && payload.daily_mean_normalised.length > 0) {
        curves[sid] = payload.daily_mean_normalised;
      }
    }
  }

  if (Object.keys(curves).length === 0) {
    return (
      <div className="text-muted small text-center py-3 border rounded">
        {isLoading ? "Loading curves…" : "No curves available."}
      </div>
    );
  }

  // Hour-of-day axis. 48 half-hour periods -> labels at every hour mark.
  const xAxisLabels = Array.from({ length: 48 }, (_, i) => {
    const h = Math.floor(i / 2);
    const m = i % 2 === 0 ? "00" : "30";
    return `${String(h).padStart(2, "0")}:${m}`;
  });
  const series = Object.entries(curves).map(([sid, data]) => {
    const isRep = sid === highlightedSiteId;
    return {
      name: namesById[sid] ?? sid,
      type: "line",
      data,
      smooth: true,
      symbol: "none",
      lineStyle: {
        width: isRep ? 3 : 1,
        opacity: isRep ? 1 : 0.4,
      },
      z: isRep ? 10 : 1,
    };
  });

  const option = {
    grid: { left: 40, right: 10, top: 10, bottom: 20 },
    tooltip: { trigger: "axis" },
    xAxis: {
      type: "category",
      data: xAxisLabels,
      axisLabel: { interval: 7 },
    },
    yAxis: {
      type: "value",
      name: "× daily mean",
      axisLabel: { formatter: (v: number) => v.toFixed(1) },
    },
    series,
  };
  return <ReactECharts option={option} style={{ height: 180 }} />;
};

export default PortfolioReviewPage;
